/**
 * Frontend Logger Service
 *
 * Environment-aware logging with module tagging and debug filtering.
 * Use createModuleLogger(name) for per-module loggers.
 */
/* global globalThis */

const LOG_LEVELS = { error: 0, warn: 1, info: 2, debug: 3 };

function getDefaultLevel() {
  if (process.env.NODE_ENV === "test") return "error";
  if (process.env.NODE_ENV === "production") return "warn";
  return "debug";
}

let currentLevel = getDefaultLevel();
let enabledModules = null; // null = all modules

function setLogLevel(level) {
  if (LOG_LEVELS[level] !== undefined) currentLevel = level;
}

function setEnabledModules(modules) {
  enabledModules = modules && modules.length > 0 ? new Set(modules) : null;
}

function isLevelEnabled(level) {
  return (LOG_LEVELS[level] ?? 0) <= (LOG_LEVELS[currentLevel] ?? 0);
}

function extractModule(args) {
  if (args.length > 0 && typeof args[0] === "string") {
    const match = args[0].match(/^\[([^\]]+)\]$/);
    if (match) return match[1];
  }
  return null;
}

function isModuleEnabled(moduleName) {
  if (!enabledModules) return true;
  return enabledModules.has(moduleName);
}

function log(level, ...args) {
  if (!isLevelEnabled(level)) return;

  const moduleName = extractModule(args);
  if (moduleName && !isModuleEnabled(moduleName)) return;

  const timestamp = new Date().toLocaleTimeString();
  const prefix = `[${timestamp}] [${level.toUpperCase()}]`;

  switch (level) {
    case "error":
      console.error(prefix, ...args);
      break;
    case "warn":
      console.warn(prefix, ...args);
      break;
    case "info":
      console.info(prefix, ...args);
      break;
    case "debug":
      console.debug(prefix, ...args);
      break;
    default:
      console.log(prefix, ...args);
  }
}

const logger = {
  error: (...args) => log("error", ...args),
  warn: (...args) => log("warn", ...args),
  info: (...args) => log("info", ...args),
  debug: (...args) => log("debug", ...args),
  setLogLevel,
  setEnabledModules,
  isLevelEnabled,
  enableDebugMode: () => setLogLevel("debug"),
  enableQuietMode: () => setLogLevel("error"),
};

function createModuleLogger(moduleName) {
  const tag = `[${moduleName}]`;
  return {
    error: (...args) => logger.error(tag, ...args),
    warn: (...args) => logger.warn(tag, ...args),
    info: (...args) => logger.info(tag, ...args),
    debug: (...args) => logger.debug(tag, ...args),
  };
}

// Expose in dev for runtime debugging
if (process.env.NODE_ENV === "development") {
  globalThis.logger = logger;
  globalThis.setLogLevel = setLogLevel;
  globalThis.setLogModules = setEnabledModules;
}

export default logger;
export { createModuleLogger, setLogLevel, setEnabledModules };
